"use client";

import { ComponentType, ReactNode, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { cn } from "@drpcrd/utils/shared";
import { Typography } from "../typography";
import { CompanyAvatar } from "../company-avatar";

export type SidebarAccountMenuItem = {
  id: string | number;
  /** Renders a divider directly above this row. */
  dividerBefore?: boolean;
} & (
  | {
      title: string;
      icon: ComponentType<{ className?: string }>;
      href?: string;
      onClick?: () => void;
      /** Optional element (e.g. a user's avatar) that cross-fades in over
       * `icon` on hover, with `icon` fading out. */
      hoverIcon?: ReactNode;
      custom?: undefined;
    }
  | {
      /** Renders this in place of the standard icon+title+link row (e.g. a
       * settings toggle). Still participates in the open/close stagger
       * animation like every other row. */
      custom: ReactNode;
      title?: undefined;
      icon?: undefined;
      href?: undefined;
      onClick?: undefined;
      hoverIcon?: undefined;
    }
);

type Props = {
  title: string;
  /** Avatar shown next to `title` in the trigger and open-panel header. */
  avatarSrc?: string | null;
  items: SidebarAccountMenuItem[];
  ctaLabel: string;
  ctaHref: string;
  ctaIcon: ComponentType<{ className?: string }>;
};

const MenuRow = ({
  title,
  icon: Icon,
  href,
  onClick,
  hoverIcon,
  custom,
  dividerBefore,
  isOpen,
  transitionDelayMs,
}: SidebarAccountMenuItem & {
  isOpen: boolean;
  transitionDelayMs: number;
}) => {
  const wrapperClassName = cn(
    "transition-all duration-200 ease-out",
    isOpen ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0",
  );
  const style = { transitionDelay: `${transitionDelayMs}ms` };

  const row = custom ? (
    <div style={style} className={wrapperClassName}>
      {custom}
    </div>
  ) : (
    (() => {
      // Enforced by `SidebarAccountMenuItem`'s discriminated union: `title`
      // and `icon` are always present on the non-`custom` branch.
      const NonNullIcon = Icon!;
      const rowContent: ReactNode = (
        <>
          <span className="relative mr-1.5 h-2 w-2 shrink-0">
            <NonNullIcon
              className={cn(
                "absolute inset-0 h-2 w-2 text-neutral-900 transition-opacity duration-200 group-hover:text-black",
                hoverIcon && "group-hover:opacity-0",
              )}
            />
            {hoverIcon && (
              <span className="absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                {hoverIcon}
              </span>
            )}
          </span>
          <Typography size="sm" weight="semibold" color="neutral" className="transition-colors group-hover:text-black">
            {title}
          </Typography>
        </>
      );
      const linkClassName = cn(
        "group mx-1 flex items-center rounded-md px-1.5 py-1 hover:bg-neutral-200",
        wrapperClassName,
      );

      return href ? (
        <Link href={href} className={linkClassName} style={style}>
          {rowContent}
        </Link>
      ) : (
        <button onClick={onClick} style={style} className={cn(linkClassName, "w-[calc(100%-1rem)] cursor-pointer text-left")}>
          {rowContent}
        </button>
      );
    })()
  );

  return (
    <>
      {dividerBefore && <hr className="mx-2 my-1 border-neutral-500" />}
      {row}
    </>
  );
};

/**
 * Pinned sidebar footer that expands upward into a floating account menu —
 * workspace identity header, a list of account-level actions (settings,
 * profile, sign out, etc.), and a "talk to us" CTA pinned at the bottom.
 * The whole header row (avatar + title + chevron) toggles the menu, the
 * panel animates open/closed by height, and the menu items reveal with a
 * staggered fade so the transition reads as progressive rather than an
 * instant swap. Clicking outside still dismisses it.
 */
export const SidebarAccountMenu = ({
  title,
  avatarSrc,
  items,
  ctaLabel,
  ctaHref,
  ctaIcon: CtaIcon,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      {/* Bottom-anchored so the box grows upward from the trigger's resting
          position — the header stays pinned to the top of the box the whole
          time, whether that box is header-only (closed) or header+panel
          (open). */}
      <div
        className={cn(
          "absolute right-0 bottom-0 left-0 z-50 flex flex-col overflow-hidden rounded-2xl border bg-white transition-[border-radius,border-color] duration-200 ease-in-out",
          isOpen ? "rounded-xl border-neutral-900" : "border-neutral-500 hover:border-neutral-900",
        )}
      >
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
          className="flex w-full shrink-0 cursor-pointer items-center gap-1 p-2 text-left"
        >
          <CompanyAvatar logoSrc={avatarSrc} name={title} size="xs" className="shrink-0" />
          <Typography size="sm" weight="semibold" className="flex-1 truncate text-left">
            {title}
          </Typography>
          <ChevronDown
            className={cn("h-2 w-2 shrink-0 text-neutral-900 transition-transform duration-200", !isOpen && "rotate-180")}
          />
        </button>

        <div
          className="grid transition-[grid-template-rows] duration-200 ease-in-out"
          style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
        >
          <div className="overflow-hidden">
            <div className="flex flex-col pb-1">
              {items.map((item, index) => (
                <MenuRow key={item.id} {...item} isOpen={isOpen} transitionDelayMs={isOpen ? index * 40 : 0} />
              ))}
            </div>

            <hr className="mx-2 mt-1 mb-2 border-neutral-500" />

            <div className="px-1.5 pb-1.5">
              <Link
                href={ctaHref}
                className={cn(
                  "bg-primary flex w-full items-center justify-center gap-1 rounded-full py-1 text-white transition-all duration-200 ease-out hover:opacity-90",
                  isOpen ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0",
                )}
                style={{ transitionDelay: isOpen ? `${items.length * 40}ms` : "0ms" }}
              >
                <CtaIcon className="h-2 w-2 shrink-0" />
                <Typography size="sm" weight="semibold" color="white">
                  {ctaLabel}
                </Typography>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Reserves the closed-state height in normal flow so surrounding
          sidebar content doesn't jump — the actual visible box above is
          absolutely positioned and grows past this spacer's height. */}
      <div className="pointer-events-none invisible flex items-center gap-1 p-2" aria-hidden="true">
        <CompanyAvatar logoSrc={avatarSrc} name={title} size="xs" className="shrink-0" />
        <Typography size="sm" weight="semibold" className="flex-1 truncate">
          {title}
        </Typography>
        <ChevronDown className="h-2 w-2 shrink-0" />
      </div>
    </div>
  );
};
